import axios, { type AxiosRequestConfig } from 'axios'

import { httpConfig } from './config'
import { normalizeHttpError } from './error'
import { tokenStorage } from './token-storage'
import type { RequestConfig } from './types'

interface RefreshEnvelope {
  code: string
  data: { access_token: string }
}

export const httpClient = axios.create(httpConfig)
let refreshPromise: Promise<string> | null = null

httpClient.interceptors.request.use((config) => {
  const requestConfig = config as typeof config & RequestConfig
  const token = tokenStorage.getAccess()
  if (token && !requestConfig.skipAuth) config.headers.Authorization = `Bearer ${token}`
  return config
})

export async function refreshAccessToken(): Promise<string> {
  refreshPromise ??= httpClient
    .post<RefreshEnvelope>('/auth/refresh', undefined, {
      skipAuth: true,
      skipRefresh: true,
    } as RequestConfig)
    .then(({ data }) => {
      if (data.code !== 'OK' || !data.data?.access_token) {
        throw new Error('会话刷新响应无效')
      }
      tokenStorage.set(data.data.access_token)
      return data.data.access_token
    })
    .finally(() => {
      refreshPromise = null
    })
  return refreshPromise
}

httpClient.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      const original = error.config as RequestConfig | undefined
      if (original && !original._retry && !original.skipRefresh && tokenStorage.getAccess()) {
        original._retry = true
        try {
          const accessToken = await refreshAccessToken()
          original.headers = { ...original.headers, Authorization: `Bearer ${accessToken}` }
          return httpClient.request(original)
        } catch {
          tokenStorage.clear()
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('video-transcript:session-expired'))
          }
        }
      }
    }
    return Promise.reject(normalizeHttpError(error))
  },
)

export async function request<T>(config: RequestConfig): Promise<T> {
  const response = await httpClient.request<T>(config as AxiosRequestConfig)
  return response.data
}
